import React, { useState, useEffect } from 'react';
import { useAuth } from '../App';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import { Card, CardContent } from './ui/card';
import { toast } from 'sonner';
import { LogOut, Briefcase, Clock, Archive, Eye, CheckCircle } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = BACKEND_URL.endsWith('/api') ? BACKEND_URL : `${BACKEND_URL}/api`;

const AdminHomepage = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [counters, setCounters] = useState({
    en_cours: 0,
    en_attente: 0,
    archive: 0
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchCounters();
  }, []);

  const fetchCounters = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API}/job-offers/count`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        setCounters(data);
      } else {
        console.error('Error fetching counters');
      }
    } catch (error) {
      console.error('Error fetching counters:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    toast.success('Déconnexion réussie');
  };

  const handleBlockClick = (blockName, blockId) => {
    if (blockId === 'admin.cours') {
      navigate('/admin/offres-en-cours');
    } else if (blockId === 'admin.attente') {
      navigate('/admin/offres-en-attente');
    } else if (blockId === 'admin.archive') {
      navigate('/admin/offres-archivees');
    } else {
      toast.info(`Navigation vers ${blockName} (${blockId}) - À implémenter`);
    }
  };

  // Company logos
  const companyLogos = {
    'Ma Conciergerie': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/mruq8p60_PNG_MA%20CONCIERGERIE.png',
    'Mon Evenementiel': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/o94kh96a_logo-monevenemenciel_png.png',
    'SISENS': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/gwqu631h_25.04.10%20SISENS%20LOGO.png',
    'Horizon Solution': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/md0676vy_Design%20sans%20titre%20%282%29.png',
    'Prysme': 'https://customer-assets.emergentagent.com/job_a2d904ae-21ae-408b-b651-ff0f492712a1/artifacts/nzxlkuw3_logo-prysme-couleur.png'
  };

  const blocks = [
    {
      id: 'admin.cours',
      title: 'Offres en cours',
      description: 'Consulter les offres d\'emploi en cours de votre société',
      icon: Briefcase,
      counter: counters.en_cours,
      color: 'bg-blue-50 hover:bg-blue-100 border-blue-200'
    },
    {
      id: 'admin.attente',
      title: 'Offres en attente',
      description: 'Visualiser les offres en attente de validation',
      icon: Clock,
      counter: counters.en_attente,
      color: 'bg-yellow-50 hover:bg-yellow-100 border-yellow-200'
    }
  ];

  const archiveBlock = {
    id: 'admin.archive',
    title: 'Offres archivées',
    description: 'Accéder aux offres archivées de votre société',
    icon: Archive,
    counter: counters.archive,
    color: 'bg-gray-50 hover:bg-gray-100 border-gray-200'
  };

  return (
    <div className="admin-homepage">
      {/* Header */}
      <header className="admin-header">
        <div className="flex items-center space-x-4">
          <img
            src={companyLogos[user?.company]}
            alt={`Logo ${user?.company}`}
            className="admin-header-logo"
            onError={(e) => {
              e.target.style.display = 'none';
            }}
          />
        </div>
        <div className="admin-header-center">
          <h1>Administration - {user?.company}</h1>
          <p className="admin-header-subtitle">Gestion et consultation des offres d'emploi</p>
        </div>
        <Button
          onClick={handleLogout}
          variant="outline"
          size="sm"
          className="btn-outline"
          style={{ borderColor: '#eb5d2d', color: '#eb5d2d' }}
        >
          <LogOut size={16} className="mr-2" />
          Déconnexion
        </Button>
      </header>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-blocks-container">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-primary"></div>
            </div>
          ) : (
            <>
              <div className="admin-blocks-grid">
                {blocks.map((block) => {
                  const IconComponent = block.icon;
                  return (
                    <Card
                      key={block.id}
                      className={`admin-block ${block.color} cursor-pointer transition-all duration-200 transform hover:scale-105`}
                      onClick={() => handleBlockClick(block.title, block.id)}
                    >
                      <CardContent className="admin-block-content">
                        <div className="admin-block-header">
                          <IconComponent className="w-8 h-8 text-primary" />
                          <div className="admin-block-counter">
                            {block.counter}
                          </div>
                        </div>
                        <h3 className="admin-block-title">{block.title}</h3>
                        <p className="admin-block-description">{block.description}</p>
                        <div className="admin-block-footer">
                          <Eye className="w-4 h-4 text-gray-500" />
                          <span className="admin-block-action">Consulter</span>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Archive Block - Full Width at Bottom */}
              <div className="admin-archive-section">
                <Card
                  className={`admin-archive-block ${archiveBlock.color} cursor-pointer transition-all duration-200`}
                  onClick={() => handleBlockClick(archiveBlock.title, archiveBlock.id)}
                >
                  <CardContent className="admin-archive-content">
                    <div className="flex items-center">
                      <div className="flex items-center mr-4">
                        <Archive className="w-6 h-6 text-primary" />
                      </div>
                      <div className="flex-1">
                        <h4 className="admin-archive-title">{archiveBlock.title}</h4>
                        <p className="admin-archive-description">{archiveBlock.description}</p>
                      </div>
                      <div className="admin-archive-counter">
                        {archiveBlock.counter}
                      </div>
                      <div className="ml-4">
                        <Eye className="w-4 h-4 text-gray-500" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </div>
            </>
          )}
        </div>
      </main>
    </div>
  );
};

export default AdminHomepage;